import { useState } from 'react';
import { Modal } from './Modal.jsx';
import { Switch } from './FormField.jsx';

const SERVICES = [
  { key: 'teams', label: 'Teams' },
  { key: 'players', label: 'Players' },
  { key: 'events', label: 'Events' },
  { key: 'bookings', label: 'Clubhouse bookings' },
  { key: 'products', label: 'Products' },
  { key: 'orders', label: 'Orders' },
  { key: 'campaigns', label: 'Email campaigns' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'lists', label: 'Lists' },
  { key: 'signupFields', label: 'Signup fields' },
  { key: 'joinRequests', label: 'Join requests' },
  { key: 'contactMessages', label: 'Contact inquiries' },
  { key: 'newsPosts', label: 'News posts' },
  { key: 'meetings', label: 'Meetings & minutes' },
  { key: 'users', label: 'Users & admins' },
  { key: 'settings', label: 'Integrations & settings' },
];

function hasR(p) { return typeof p === 'string' && p.includes('r'); }
function hasW(p) { return typeof p === 'string' && p.includes('w'); }
function composeP(r, w) {
  if (r && w) return 'rw';
  if (r) return 'r';
  if (w) return 'w';
  return '';
}

// Reusable permissions editor. Used for per-user and for new-admin defaults.
export function PermissionsEditor({ open, initial, title, eyebrow, onClose, onSave }) {
  // `initial` may be undefined (legacy) → treat as full access.
  const legacy = initial == null;
  const [all, setAll] = useState(legacy || !!initial?.all);
  const [perms, setPerms] = useState(() => {
    const seed = {};
    for (const s of SERVICES) seed[s.key] = initial?.[s.key] || '';
    return seed;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function update(key, r, w) {
    setPerms((p) => ({ ...p, [key]: composeP(r, w) }));
  }
  function setAllReadOnly() {
    const next = {}; for (const s of SERVICES) next[s.key] = 'r';
    setPerms(next); setAll(false);
  }
  function clearAll() {
    const next = {}; for (const s of SERVICES) next[s.key] = '';
    setPerms(next); setAll(false);
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      const payload = all
        ? { all: true }
        : Object.fromEntries(Object.entries(perms).filter(([, v]) => v));
      await onSave(payload);
    } catch (e) {
      setErr(e.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={eyebrow}
      title={title}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="button" onClick={save} className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save permissions'}
          </button>
        </>
      }
    >
      {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
      <div className="space-y-5">
        <div className="rounded-md border border-line bg-canvas/50 p-4">
          <Switch
            checked={all}
            onChange={setAll}
            label="Full access (super-admin)"
            hint="Grants read + write on every service. Overrides everything below."
          />
        </div>
        {!all && (
          <>
            <div className="flex items-center gap-2">
              <button type="button" className="btn-outline btn-xs" onClick={setAllReadOnly}>All read-only</button>
              <button type="button" className="btn-outline btn-xs" onClick={clearAll}>Clear all</button>
            </div>
            <div className="card-flush">
              <div className="hidden md:grid grid-cols-[1fr_auto_auto] gap-6 items-center px-4 py-3 border-b border-line">
                <span className="eyebrow">Service</span>
                <span className="eyebrow text-center w-14">Read</span>
                <span className="eyebrow text-center w-14">Write</span>
              </div>
              <div className="divide-rule">
                {SERVICES.map((s) => {
                  const p = perms[s.key];
                  const r = hasR(p);
                  const w = hasW(p);
                  return (
                    <div key={s.key} className="grid grid-cols-[1fr_auto_auto] gap-6 items-center px-4 py-3">
                      <span className="text-sm">{s.label}</span>
                      <label className="w-14 flex justify-center cursor-pointer">
                        <input type="checkbox" className="check" checked={r} onChange={(e) => update(s.key, e.target.checked, w)} />
                      </label>
                      <label className="w-14 flex justify-center cursor-pointer">
                        <input type="checkbox" className="check" checked={w} onChange={(e) => update(s.key, r, e.target.checked)} />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-[11px] text-ink-3">
              Write implies the ability to create, edit, and delete. Read is list/view only.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

export function summarizePermissions(perms) {
  if (!perms || Object.keys(perms).length === 0) return 'no access';
  if (perms.all) return 'Full access';
  const services = Object.entries(perms).filter(([k, v]) => k !== 'all' && v).length;
  return `${services} service${services === 1 ? '' : 's'}`;
}
